import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import SectionTabs from "@/components/SectionTabs";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Clock, Eye, CalendarDays, Trophy, Users, Heart, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { labelEventType } from "@/lib/roleCategories";
const TYPE_LABEL = new Proxy({}, { get: (_, k) => labelEventType(k) });

function eventCover(e) {
  if (e.cover_image) return `${API}/img/${e.cover_image}?w=1600&q=86`;
  if (e.image_url) return e.image_url;
  return null;
}

export default function MyEvents() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) navigate("/");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    api.get("/my/events")
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch((err) => toast.error(err.response?.data?.detail || "Erreur de chargement"))
      .finally(() => setLoading(false));
  }, []);

  if (!user) return null;

  return (
    <AppShell>
      <SectionTabs
        eyebrow="L'agenda"
        title="Mes événements"
        subtitle="Tous les événements Helencia auxquels vous participez."
        tabs={[
          { id: "events", to: "/events", label: "Événements", icon: CalendarDays },
          { id: "concours", to: "/talent/concours", label: "Concours", icon: Trophy },
          { id: "my-entries", to: "/talent/my-contest-entries", label: "Mes participations", icon: Heart },
          { id: "my-events", to: "/talent/my-events", label: "Mes événements", icon: Calendar },
          { id: "castings", to: "/talent/castings", label: "Castings", icon: Users },
        ]}
      />

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-[#C68E59] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl bg-white border border-dashed border-[#EADDC9] p-12 text-center" data-testid="my-events-empty">
          <Calendar className="w-10 h-10 text-[#C68E59] mx-auto opacity-60" />
          <p className="text-sm text-[#8B8680] mt-4">Vous n'êtes inscrit·e à aucun événement pour le moment.</p>
          <Link to="/events" className="inline-flex mt-5">
            <Button className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white">
              Découvrir les événements
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="my-events-grid">
          {items.map((e) => (
            <EventRow key={e.id} event={e} />
          ))}
        </div>
      )}
    </AppShell>
  );
}

function EventRow({ event }) {
  const cover = eventCover(event);
  const past = event.status === "past";
  return (
    <div className="bg-white rounded-2xl border border-[#EADDC9] overflow-hidden flex flex-col" data-testid={`my-event-${event.id}`}>
      <Link to={`/events/${event.id}`} className="block group">
        <div className="aspect-[16/10] overflow-hidden bg-[#FAF3EA] relative">
          {cover ? (
            <img src={cover} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#B3A78C] text-xs">Sans image</div>
          )}
          <span className="absolute top-2 left-2 inline-flex bg-white/95 backdrop-blur px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold text-[#1A1A1A]">
            {TYPE_LABEL[event.event_type] || event.event_type}
          </span>
          {past ? (
            <span className="absolute top-2 right-2 inline-flex bg-[#FAF3EA] text-[#8B8680] px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border border-[#E5E5E5]">
              Passé
            </span>
          ) : (
            <span className="absolute top-2 right-2 inline-flex bg-[#E7F5EC] text-[#2E7D32] px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border border-[#BFE4C5]">
              À venir
            </span>
          )}
        </div>
      </Link>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-serif text-xl text-[#1A1A1A] leading-tight line-clamp-2">{event.title}</h3>
        <div className="text-xs text-[#666] mt-3 space-y-1">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3 h-3" />
            {event.date && new Date(event.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
          </div>
          {event.time && (
            <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {event.time}</div>
          )}
          {event.location && (
            <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {event.location}</div>
          )}
        </div>
        <Link to={`/events/${event.id}`} className="mt-4 mt-auto" data-testid={`my-event-view-${event.id}`}>
          <Button variant="outline" size="sm" className="w-full rounded-full border-[#EADDC9]">
            <Eye className="w-3.5 h-3.5 mr-1.5" /> Voir le détail
            <ArrowRight className="w-3 h-3 ml-1.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
